/**
 * Common English letter pairs and triples with their approximate frequencies (percent of
 * all pairs or triples), used only to rank candidate decodings of a transposition.
 *
 * The figures are the widely reproduced bigram and trigram tables derived from Peter
 * Norvig's corpus counts (TH ≈ 3.6 %, HE ≈ 3.1 %, THE ≈ 1.8 %). They are rounded and
 * differ a little between corpora; a ranking needs only their rough order, and it stays a
 * hint on short messages.
 */
export const ENGLISH_BIGRAMS: Readonly<Record<string, number>> = {
  TH: 3.56,
  HE: 3.07,
  IN: 2.43,
  ER: 2.05,
  AN: 1.99,
  RE: 1.85,
  ON: 1.76,
  AT: 1.49,
  EN: 1.45,
  ND: 1.35,
  TI: 1.34,
  ES: 1.34,
  OR: 1.28,
  TE: 1.2,
  OF: 1.17,
  ED: 1.17,
  IS: 1.13,
  IT: 1.12,
  AL: 1.09,
  AR: 1.07,
  ST: 1.05,
  TO: 1.04,
  NT: 1.04,
  NG: 0.95,
  SE: 0.93,
  HA: 0.93,
  AS: 0.87,
  OU: 0.87,
  IO: 0.83,
  LE: 0.83,
  VE: 0.83,
  CO: 0.79,
  ME: 0.79,
  DE: 0.76,
  HI: 0.76,
  RI: 0.73,
  RO: 0.73,
  IC: 0.7,
  NE: 0.69,
  EA: 0.69,
  RA: 0.69,
  CE: 0.65,
  LI: 0.62,
  CH: 0.6,
  LL: 0.58,
  BE: 0.58,
  MA: 0.57,
  SI: 0.55,
  OM: 0.55,
  UR: 0.54,
};

export const ENGLISH_TRIGRAMS: Readonly<Record<string, number>> = {
  THE: 1.81,
  AND: 0.73,
  ING: 0.72,
  ENT: 0.42,
  ION: 0.42,
  HER: 0.36,
  FOR: 0.34,
  THA: 0.33,
  NTH: 0.33,
  INT: 0.32,
  ERE: 0.31,
  TIO: 0.31,
  VER: 0.28,
  EST: 0.28,
  ALL: 0.26,
  ATI: 0.26,
  HAT: 0.25,
  WIT: 0.25,
};

/**
 * How English-like a run of letters looks: the common pairs and triples it contains,
 * weighted by frequency, per pair. Higher is more English-like; only the order of scores
 * for texts of the same length means anything.
 */
export function englishPairScore(text: string): number {
  const letters = [...text.toUpperCase()]
    .filter((char) => char >= "A" && char <= "Z")
    .join("");
  if (letters.length < 2) {
    return 0;
  }
  let total = 0;
  for (let index = 0; index + 1 < letters.length; index += 1) {
    total += ENGLISH_BIGRAMS[letters.slice(index, index + 2)] ?? 0;
    total += ENGLISH_TRIGRAMS[letters.slice(index, index + 3)] ?? 0;
  }
  return total / (letters.length - 1);
}
